/**
 * Error Parser Service
 *
 * Parses raw error messages and returns user-friendly messages
 * based on detected patterns from OpenAI SDK and other sources.
 */

export function parseErrorMessage(error: unknown): string {
  if (!error) {
    return 'An unknown error occurred'
  }

  // Extract message from Error object if needed
  const errorMessage = typeof error === 'string' ? error : (error as Error).message || String(error)
  const lowerMessage = errorMessage.toLowerCase()

  // Configuration-related errors
  if (lowerMessage.includes('configuration') ||
      lowerMessage.includes('not configured') ||
      lowerMessage.includes('not initialized') ||
      lowerMessage.includes('api key') && (lowerMessage.includes('missing') || lowerMessage.includes('invalid'))) {
    return 'AI service is not properly configured. Please check your settings and ensure API keys are set up correctly.'
  }

  // Authentication errors
  if (lowerMessage.includes('authentication') ||
      lowerMessage.includes('unauthorized') ||
      lowerMessage.includes('invalid api key') ||
      lowerMessage.includes('incorrect api key')) {
    return 'Authentication failed. Please check your API key in the settings.'
  }

  // Rate limiting errors
  if (lowerMessage.includes('rate limit') ||
      lowerMessage.includes('too many requests') ||
      lowerMessage.includes('quota exceeded')) {
    return 'Rate limit exceeded. Please wait a moment and try again.'
  }

  // Connection errors
  if (lowerMessage.includes('connection') ||
      lowerMessage.includes('network') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('failed to fetch')) {
    return 'Connection error. Please check your internet connection and try again.'
  }

  // API-specific errors
  if (lowerMessage.includes('api error') ||
      lowerMessage.includes('service unavailable') ||
      lowerMessage.includes('internal server error')) {
    return 'The AI service is currently unavailable. Please try again later.'
  }

  // Model-related errors
  if (lowerMessage.includes('model') &&
      (lowerMessage.includes('not found') || lowerMessage.includes('invalid'))) {
    return 'The selected AI model is not available. Please choose a different model in settings.'
  }

  // Context window/token limit errors
  if (lowerMessage.includes('token') &&
      (lowerMessage.includes('limit') || lowerMessage.includes('exceeded') || lowerMessage.includes('too long'))) {
    return 'The conversation is too long. Please start a new conversation or try a shorter message.'
  }

  // Default fallback message
  return 'An unexpected error occurred. Please try again.'
}

export function isRecoverableError(error: unknown): boolean {
  if (!error) return false

  const errorMessage = typeof error === 'string' ? error : (error as Error).message || String(error)
  const lowerMessage = errorMessage.toLowerCase()

  // These errors are typically recoverable
  const recoverablePatterns = [
    'rate limit',
    'connection',
    'network',
    'timeout',
    'service unavailable',
    'internal server error'
  ]

  return recoverablePatterns.some(pattern => lowerMessage.includes(pattern))
}

export type ErrorSeverity = 'info' | 'warning' | 'error' | 'critical'

export function getErrorSeverity(error: unknown): ErrorSeverity {
  if (!error) return 'error'

  const errorMessage = typeof error === 'string' ? error : (error as Error).message || String(error)
  const lowerMessage = errorMessage.toLowerCase()

  // Critical errors that require immediate attention
  if (lowerMessage.includes('configuration') ||
      lowerMessage.includes('authentication') ||
      lowerMessage.includes('api key')) {
    return 'critical'
  }

  // Warnings for rate limits and temporary issues
  if (lowerMessage.includes('rate limit') ||
      lowerMessage.includes('service unavailable')) {
    return 'warning'
  }

  // Info for connection issues that might resolve
  if (lowerMessage.includes('connection') ||
      lowerMessage.includes('network')) {
    return 'info'
  }

  // Default to error
  return 'error'
}

export default {
  parseErrorMessage,
  isRecoverableError,
  getErrorSeverity
}
